import { create } from 'zustand';
import { nanoid } from 'nanoid';
import type {
  AwardedQuotation,
  Client,
  ID,
  LaborRolePreset,
  Project,
  Quotation,
  QuotationKind,
  QuotationScopeCategory,
  QuotationVersion,
  SalesContact,
  ScopeBundle,
} from '../types/Quotation';
import { seedClients, seedSalesContacts } from '../data/quotationClients';
import { seedLaborPresets, starterGeneralReqts } from '../data/quotationPresets';
import { quotationCode } from '../utils/calcsheet/codes';
import { awardFigures } from '../utils/calcsheet/awardFigures';
import type { InstallMaterialPrice } from '../utils/calcsheet/installationMaterials';
import { getOneDriveTokenStore } from '../services/onedriveTokenStore';
import { isCorporateOneDriveConfigured } from '../config/onedriveConfig';
import { ensureProposalFolder, ensureWonExecutionLayout } from '../services/onedriveFolderService';
import { API_BASE } from '../config/api';

// API base — talks to pmv2's Express server
// Dev: the host the page was loaded from (so a phone on the LAN reaches the API); prod: same origin. See config/api.ts.
const BASE = `${API_BASE}/api/calcsheet`;

const normalizeLaborPresets = (presets: LaborRolePreset[]): LaborRolePreset[] =>
  presets.map((p) => {
    if (
      p.group === 'labor' &&
      p.allowance === 250 &&
      p.dailyRate === 1500 &&
      ['Technician', 'Electrician', 'Safety Officer'].includes(p.role)
    ) {
      return { ...p, dailyRate: 1200 };
    }
    return p;
  });

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('netpacific_token');
  return token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
}

async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  // Paths starting with /api/ are absolute (hit the pmv2-wide endpoint, e.g. /api/clients);
  // everything else is relative to the calcsheet base (/api/calcsheet/...).
  const url = path.startsWith('/api/') ? `${API_BASE}${path}` : `${BASE}${path}`;
  const res = await fetch(url, {
    method,
    headers: authHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error: string }).error || 'API error');
  }
  return res.json();
}

export interface CalcsheetSettings {
  defaultJobTitles?: {
    IOCT?: string;
    ACTI?: string;
  };
}

interface State {
  clients: Client[];
  salesContacts: SalesContact[];
  laborPresets: LaborRolePreset[];
  scopeBundles: ScopeBundle[];
  // Unit pricing for the Installation Work calculator's fixed material slots
  // (see utils/calcsheet/installationMaterials.ts). Keyed by slot key; a slot
  // absent here just means it hasn't been priced yet (unitCost 0 in the UI).
  installMaterialPrices: Record<string, InstallMaterialPrice>;
  projects: Project[];
  quotations: Quotation[];
  seq: number;
  settings: CalcsheetSettings;
  initialized: boolean;
}

interface Actions {
  // Hydrate store from API (call once on mount)
  init: () => Promise<void>;

  // Clients
  addClient: (c: Omit<Client, 'id'>) => Promise<Client>;
  updateClient: (id: ID, patch: Partial<Client>) => Promise<void>;
  deleteClient: (id: ID) => Promise<void>;

  // Projects
  addProject: (p: Omit<Project, 'id' | 'code' | 'createdAt' | 'updatedAt'> & { code?: string }) => Promise<Project>;
  updateProject: (id: ID, patch: Partial<Project>) => Promise<void>;
  deleteProject: (id: ID) => Promise<void>;
  syncMainProject: (id: ID, opts?: { force?: boolean }) => Promise<SyncMainProjectResult>;
  // Lock the awarded quotation's price/budget on the project (and its linked Project List row).
  awardQuotation: (projectId: ID, quotationId: ID) => Promise<AwardResult>;
  // Admin-only: release the price lock.
  unawardProject: (projectId: ID) => Promise<void>;

  // Quotations
  createQuotation: (projectId: ID, kind: QuotationKind, recipientId: ID | null, scopeCategory?: QuotationScopeCategory) => Promise<Quotation>;
  // `remarks` is an optional changelog note for this save — recorded on the
  // version snapshot the server takes of the pre-save state, not stored on
  // the quotation itself.
  updateQuotation: (id: ID, patch: Partial<Quotation>, remarks?: string) => Promise<Quotation>;
  deleteQuotation: (id: ID) => Promise<void>;
  duplicateQuotation: (id: ID, opts?: { projectId?: ID }) => Promise<Quotation | null>;
  // Import a fully-formed Quotation (e.g. from a legacy Excel parse) under an existing project.
  importQuotation: (q: Quotation) => Promise<Quotation>;
  // Saved-version history for a quotation (newest first). Not cached in the store —
  // fetched on demand when the History dialog opens.
  fetchQuotationVersions: (id: ID) => Promise<QuotationVersion[]>;
  deleteQuotationVersion: (quotationId: ID, versionId: ID) => Promise<void>;

  // Presets
  addPreset: (p: Omit<LaborRolePreset, 'id'>) => Promise<LaborRolePreset>;
  updatePreset: (id: ID, patch: Partial<LaborRolePreset>) => Promise<void>;
  deletePreset: (id: ID) => Promise<void>;
  resetPresets: () => Promise<void>;

  // Scope Library (reusable inclusion bundles)
  addScopeBundle: (b: Omit<ScopeBundle, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'createdByName'>) => Promise<ScopeBundle>;
  updateScopeBundle: (id: ID, patch: Partial<ScopeBundle>) => Promise<void>;
  deleteScopeBundle: (id: ID) => Promise<void>;

  // Installation Work calculator — material unit pricing
  updateInstallMaterialPrice: (key: string, patch: InstallMaterialPrice) => Promise<void>;

  // Settings
  updateSettings: (patch: CalcsheetSettings) => Promise<void>;

  resetAll: () => Promise<void>;
}

export interface AwardResult {
  awarded: AwardedQuotation;
  mainProject: { id: string; previousAmount: number; amount: number; budget: number } | null;
}

export interface SyncMainProjectResult {
  success: boolean;
  action: 'created' | 'recreated' | 'updated' | 'linked-existing' | 'amount-synced';
  mainProjectId: string;
  projectNo: string;
  quotationId: string;
  quotationKind: QuotationKind;
  amount: number;
  previousAmount?: number;
}

const now = () => new Date().toISOString();

function normalizeNameKey(value: string | undefined | null): string {
  return (value || '').trim().toLowerCase();
}

function firstLastKey(value: string | undefined | null): string {
  const parts = normalizeNameKey(value).split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  return `${parts[0]} ${parts[parts.length - 1]}`;
}

function mergeSalesContactsWithUsers(
  seed: SalesContact[],
  users: Array<{ id: string; full_name?: string | null; username?: string; email?: string | null; designation?: string | null; contact_number?: string | null }>,
): SalesContact[] {
  const merged = [...seed];
  for (const user of users) {
    const name = (user.full_name || '').trim();
    if (!name) continue;
    const userNameKey = normalizeNameKey(name);
    const userFirstLast = firstLastKey(name);
    const userEmail = (user.email || '').trim();
    const idx = merged.findIndex((contact) => {
      const contactNameKey = normalizeNameKey(contact.name);
      const contactFirstLast = firstLastKey(contact.name);
      return (
        (!!userNameKey && contactNameKey === userNameKey) ||
        (!!userFirstLast && contactFirstLast === userFirstLast) ||
        (!!userEmail && normalizeNameKey(contact.email) === normalizeNameKey(userEmail))
      );
    });
    const patch = {
      id: user.id,
      name,
      position: (user.designation || '').trim(),
      email: userEmail,
      phone: (user.contact_number || '').trim(),
    };
    if (idx >= 0) {
      merged[idx] = {
        ...merged[idx],
        position: patch.position || merged[idx].position,
        // Email is the opposite priority from position/phone: a hand-curated
        // seed email (e.g. tj@iocontroltech.com) is the deliberate signing
        // identity for PDFs and must win over whatever's on the person's
        // Firestore user account (which may be a personal/login address).
        email: merged[idx].email || patch.email,
        phone: patch.phone || merged[idx].phone,
      };
    } else {
      merged.push(patch);
    }
  }
  return merged;
}

// IOCT is a non-VAT taxpayer (percentage tax), so new IOCT quotations start with
// Non-VAT on. Flip this to false when IOCT registers for VAT — the VAT-EX /
// VAT-IN options stay available per quotation either way.
const IOCT_NON_VAT_DEFAULT = true;

const blankQuotation = (
  projectId: ID,
  kind: QuotationKind,
  recipientId: ID | null,
  id: string,
  // Default name to pre-fill the Prepared by / Authorized by Autocomplete fields.
  // Caller passes the project's account-manager name (resolved from
  // `project.salesContactId` against `salesContacts`). Both fields remain
  // editable inline on the quotation; this only sets the seed value so the
  // PDF "Prepared by:" defaults to the AM without manual intervention.
  defaultSignatoryName: string = '',
  scopeCategory: QuotationScopeCategory = 'both',
): Quotation => ({
  id,
  projectId,
  kind,
  scopeCategory,
  revision: '00',
  recipientId,
  validityDays: 30,
  paymentTerms: '30% Downpayment, 70% Progress Billing',
  deliveryTerms: 'Delivery is 1-2 weeks, upon receipt of a technically and commercially clarified purchase order.',
  warrantyMonths: 12,
  productMarkupPct: 30,
  productContingencyPct: 0,
  laborMarkupPct: 100,
  generalReqMarkupPct: 0,
  // 5% EWT gross-up on new IOCT quotations only (ACTI quotes never get a
  // default — see the Quotation.ewtPct doc comment).
  ewtPct: kind === 'IOCT' ? 5 : undefined,
  globalContingencyPct: 0,
  discountPct: 0,
  vatPct: 0,
  // ACTI quotations keep the existing VAT behaviour.
  nonVat: kind === 'IOCT' && IOCT_NON_VAT_DEFAULT,
  // Any scope that can include physical goods (Supply only, or Both) defaults
  // the delivery fee + minimum-order surcharge on — the team can still switch
  // it off inline (this is just the starting value, not locked like the
  // section editability above). Services-only starts off, since there's
  // nothing to deliver.
  deliveryTermsEnabled: scopeCategory !== 'services',
  // Supply-only locks Section A in the editor (see CalcsheetQuotationEditor's
  // generalReqtsLocked) — seeding it with starter rows there would create a
  // quotation that's already "stuck": data visible in a section the team can
  // neither edit nor clear. Every other scope keeps the usual starter rows.
  generalReqts: scopeCategory === 'supply' ? [] : starterGeneralReqts(),
  components: [],
  services: [],
  manpower: [],
  servicesFromManpower: true,
  engineeringServicesQty: 1,
  exportGeneralReqtsAsLot: true,
  generalReqtsExportQty: 1,
  pageBreakBeforeTerms: false,
  preparedBy: defaultSignatoryName,
  authorizedBy: defaultSignatoryName,
  createdAt: now(),
  updatedAt: now(),
});

// In-flight guard: `initialized` only flips true after init's awaits resolve,
// so two concurrent init() calls (e.g. React StrictMode's double-invoked
// effects in dev) would both pass the state guard and run the empty-presets
// seed-and-persist twice, creating duplicate presets. Share one promise instead.
let initInFlight: Promise<void> | null = null;

export const useQuotationStore = create<State & Actions>()((set, get) => ({
  clients: seedClients(),
  salesContacts: seedSalesContacts(),
  laborPresets: seedLaborPresets(),
  scopeBundles: [],
  installMaterialPrices: {},
  projects: [],
  quotations: [],
  seq: 1,
  settings: {},
  initialized: false,

  init: async () => {
    if (get().initialized) return;
    if (initInFlight) return initInFlight;
    initInFlight = (async () => {
    try {
      const [pRes, qRes, cRes, prRes, sRes, staffRes, stRes, slRes, impRes] = await Promise.all([
        api<{ projects: Project[] }>('GET', '/projects'),
        api<{ quotations: Quotation[] }>('GET', '/quotations'),
        api<{ clients: Client[] }>('GET', '/clients'),
        api<{ presets: LaborRolePreset[] }>('GET', '/presets'),
        api<{ seq: number }>('GET', '/seq'),
        api<{ contacts: Array<{ id: string; full_name?: string | null; username?: string; email?: string | null; designation?: string | null; contact_number?: string | null }> }>('GET', '/api/users/staff-contacts').catch(() => ({ contacts: [] })),
        api<{ settings: CalcsheetSettings }>('GET', '/settings').catch(() => ({ settings: {} })),
        // Scope Library is optional — an older server without the endpoint must
        // not break init, so swallow failures into an empty library.
        api<{ bundles: ScopeBundle[] }>('GET', '/scope-library').catch(() => ({ bundles: [] })),
        // Same for Installation Work material pricing — sparse and optional.
        api<{ prices: Record<string, InstallMaterialPrice> }>('GET', '/install-materials').catch(() => ({ prices: {} })),
      ]);
      const salesContacts = mergeSalesContactsWithUsers(seedSalesContacts(), staffRes.contacts ?? []);
      let laborPresets: LaborRolePreset[];
      if (prRes.presets.length) {
        laborPresets = normalizeLaborPresets(prRes.presets);
      } else {
        // First run — the presets collection is empty. Persist the defaults once
        // so their ids are stable Firestore ids on every later load. An in-memory
        // seed would mint fresh nanoids each load, orphaning any manpower
        // `presetId` that referenced them. Best-effort: if a POST fails (offline
        // / not authed), fall back to the in-memory seed for that row.
        const seeds = seedLaborPresets();
        laborPresets = normalizeLaborPresets(
          await Promise.all(
            seeds.map(({ id: _drop, ...body }) =>
              api<{ preset: LaborRolePreset }>('POST', '/presets', body)
                .then((r) => r.preset ?? { id: _drop, ...body })
                .catch(() => ({ id: _drop, ...body })),
            ),
          ),
        );
      }
      set({
        projects: pRes.projects ?? [],
        quotations: qRes.quotations ?? [],
        clients: cRes.clients.length ? cRes.clients : seedClients(),
        salesContacts,
        laborPresets,
        scopeBundles: slRes.bundles ?? [],
        installMaterialPrices: impRes.prices ?? {},
        seq: sRes.seq ?? 1,
        settings: stRes.settings ?? {},
        initialized: true,
      });
    } catch {
      // API unavailable — fall back to seed data (offline/local dev without server)
      set({ initialized: true });
    } finally {
      initInFlight = null;
    }
    })();
    return initInFlight;
  },

  // ── Clients (unified `/api/clients` — see consolidation) ──────────────────

  addClient: async (c) => {
    // The unified endpoint returns the full doc on POST.
    const saved = await api<Client>('POST', '/api/clients', c);
    set({ clients: [...get().clients, saved] });
    return saved;
  },
  updateClient: async (id, patch) => {
    const cleaned: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(patch)) if (v !== undefined) cleaned[k] = v;
    await api('PUT', `/api/clients/${id}`, cleaned);
    set({ clients: get().clients.map((c) => (c.id === id ? { ...c, ...patch } : c)) });
  },
  deleteClient: async (id) => {
    await api('DELETE', `/api/clients/${id}`);
    set({ clients: get().clients.filter((c) => c.id !== id) });
  },

  // ── Projects ───────────────────────────────────────────────────────────────

  addProject: async (p) => {
    const customer = get().clients.find((c) => c.id === p.customerId);
    const { code: codeOverride, ...rest } = p;
    const explicitCode = (codeOverride || '').trim();
    // A project can be created before its client is known (a bare opportunity
    // placeholder). Codes are derived from the client's code, so without a
    // client — and without an explicit override — skip both code generation
    // AND the sequence increment (sequences are global/precious; don't burn
    // one on a draft that has nothing to encode yet). The code is assigned
    // later, in updateProject, the first time a customer is set.
    let code = explicitCode;
    let seq = get().seq;
    let incrementedSeq = false;
    if (!code && p.customerId) {
      seq = await api<{ seq: number }>('POST', '/seq/increment').then((r) => r.seq).catch(() => get().seq);
      incrementedSeq = true;
      code = quotationCode(seq, customer?.code ?? 'XXX', '00', new Date(p.date));
    }
    const project: Project = {
      id: nanoid(8),
      code,
      createdAt: now(),
      updatedAt: now(),
      ...rest,
    };

    // When corporate OneDrive is configured we try to create/link the proposal
    // folder up front so the stored project carries the reference from the start.
    // This is BEST-EFFORT: if OneDrive isn't signed in, or a token can't be
    // acquired silently (e.g. the SPA refresh token expired after Azure's 24h cap
    // and ssoSilent is blocked by third-party-cookie policy), we still save the
    // project WITHOUT a folder. The user can sign in and create/link the proposal
    // folder later from the project detail page. Never block project creation on
    // OneDrive — a tokenless-but-cached MSAL session must not strand the user.
    let projectWithFolder = project;
    if (code && isCorporateOneDriveConfigured()) {
      try {
        const odStore = getOneDriveTokenStore();
        const token = odStore.isAuthenticated ? await odStore.getToken() : null;
        if (token) {
          const ref = await ensureProposalFolder(token, project);
          projectWithFolder = {
            ...project,
            proposalFolderId: ref.id,
            proposalFolderUrl: ref.webUrl,
          };
        } else {
          // eslint-disable-next-line no-console
          console.warn(
            '[OneDrive] No token at project create (signed in:',
            odStore.isAuthenticated,
            ') — saving project without a proposal folder; link it later from the project page.',
          );
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(
          '[OneDrive] Proposal-folder creation failed at project create — saving project without a folder; link it later.',
          e,
        );
      }
    }

    const res = await api<{ project: Project }>('POST', '/projects', projectWithFolder);
    const saved = res.project ?? projectWithFolder;
    set({ projects: [...get().projects, saved], ...(incrementedSeq ? { seq: seq + 1 } : {}) });
    return saved;
  },
  updateProject: async (id, patch) => {
    const prev = get().projects.find((p) => p.id === id);
    // A project created without a client (see addProject) has no code yet.
    // The first time a customer is set on it, assign a code now — unless the
    // caller is itself explicitly setting a custom code in the same patch.
    let effectivePatch: Partial<Project> = patch;
    const callerProvidedCode = typeof patch.code === 'string' && patch.code.trim().length > 0;
    if (prev && !prev.code && patch.customerId && !callerProvidedCode) {
      const customer = get().clients.find((c) => c.id === patch.customerId);
      const seq = await api<{ seq: number }>('POST', '/seq/increment').then((r) => r.seq).catch(() => get().seq);
      const dateForCode = patch.date ?? prev.date;
      const code = quotationCode(seq, customer?.code ?? 'XXX', '00', new Date(dateForCode));
      effectivePatch = { ...patch, code };
      set({ seq: seq + 1 });
    }
    const cleaned: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(effectivePatch)) if (v !== undefined) cleaned[k] = v;
    await api('PUT', `/projects/${id}`, cleaned);
    set({
      projects: get().projects.map((p) =>
        p.id === id ? { ...p, ...effectivePatch, updatedAt: now() } : p,
      ),
    });

    // OneDrive execution layout (best-effort, fire-and-forget):
    //  1. status → won: promote/create under 01 Execution with IOCT naming when
    //     mainProjectNo is known (`IOCT2605001-LBI Project Name`).
    //  2. mainProjectNo arrives later on an already-won project: rename the
    //     existing execution folder from PCS… → IOCT… if needed.
    const next: Project | undefined = prev ? { ...prev, ...patch } : undefined;
    const becameWon = !!patch.status && patch.status === 'won' && !!prev && prev.status !== 'won';
    const mainNoArrived =
      typeof patch.mainProjectNo === 'string' &&
      !!patch.mainProjectNo &&
      patch.mainProjectNo !== (prev?.mainProjectNo || '');
    const isWon = (next?.status === 'won') || becameWon;
    const shouldEnsureExecution =
      isCorporateOneDriveConfigured() &&
      !!next &&
      isWon &&
      (becameWon || mainNoArrived || (!!next.mainProjectNo && !next.executionFolderId));

    if (shouldEnsureExecution && next) {
      (async () => {
        try {
          const odStore = getOneDriveTokenStore();
          if (!odStore.isAuthenticated) return;
          const token = await odStore.getToken();
          if (!token) return;

          const { executionFolder, proposalFolder } = await ensureWonExecutionLayout(token, next);
          const patchFields: Partial<Project> = {
            executionFolderId: executionFolder.id,
            executionFolderUrl: executionFolder.webUrl,
            ...(proposalFolder?.webUrl ? { proposalFolderUrl: proposalFolder.webUrl } : {}),
          };
          const cur = get().projects.find((p) => p.id === id);
          if (
            cur?.executionFolderId === patchFields.executionFolderId &&
            cur?.executionFolderUrl === patchFields.executionFolderUrl &&
            (!patchFields.proposalFolderUrl || cur?.proposalFolderUrl === patchFields.proposalFolderUrl)
          ) {
            // Nothing to persist (already correct / no URL change after rename).
            // Still write when rename changes the webUrl.
          }
          await api('PUT', `/projects/${id}`, patchFields);
          // Only set on the linked main project if it has no execution folder yet.
          if (next.mainProjectId) {
            const mainResp = await fetch(`/api/projects/${next.mainProjectId}`).then((r) => r.json()).catch(() => null);
            if (mainResp && !mainResp.executionFolderId) {
              await api('PUT', `/api/projects/${next.mainProjectId}`, {
                executionFolderId: executionFolder.id,
                executionFolderUrl: executionFolder.webUrl,
              }).catch(() => {});
            }
          }
          set({
            projects: get().projects.map((proj) =>
              proj.id === id ? { ...proj, ...patchFields } : proj,
            ),
          });
          // eslint-disable-next-line no-console
          console.info('[OneDrive] execution folder ensured', executionFolder.webUrl || executionFolder.name);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[OneDrive] execution promotion/rename failed (non-blocking)', err);
        }
      })();
    }
  },
  deleteProject: async (id) => {
    await api('DELETE', `/projects/${id}`);
    set({
      projects: get().projects.filter((p) => p.id !== id),
      quotations: get().quotations.filter((q) => q.projectId !== id),
    });
  },
  awardQuotation: async (projectId, quotationId) => {
    const quotation = get().quotations.find((q) => q.id === quotationId);
    if (!quotation) throw new Error('Quotation not found');
    const figures = awardFigures(quotation);
    const res = await api<AwardResult & { success: boolean }>('POST', `/projects/${projectId}/award`, {
      quotationId,
      price: figures.price,
      grandTotal: figures.grandTotal,
      budget: figures.budget,
    });
    const { awarded } = res;
    set({
      projects: get().projects.map((p) => (p.id === projectId ? { ...p, awarded, updatedAt: now() } : p)),
      quotations: get().quotations.map((q) => {
        if (q.projectId !== projectId) return q;
        if (q.id === quotationId) return { ...q, awardedAt: awarded.awardedAt, awardedBy: awarded.awardedBy, awardedById: awarded.awardedById };
        if (!q.awardedAt) return q;
        const { awardedAt: _a, awardedBy: _b, awardedById: _c, ...rest } = q;
        return rest as Quotation;
      }),
    });
    return { awarded, mainProject: res.mainProject ?? null };
  },
  unawardProject: async (projectId) => {
    await api('POST', `/projects/${projectId}/unaward`, {});
    set({
      projects: get().projects.map((p) => {
        if (p.id !== projectId) return p;
        const { awarded: _a, ...rest } = p;
        return { ...rest, updatedAt: now() } as Project;
      }),
      quotations: get().quotations.map((q) => {
        if (q.projectId !== projectId || !q.awardedAt) return q;
        const { awardedAt: _a, awardedBy: _b, awardedById: _c, ...rest } = q;
        return rest as Quotation;
      }),
    });
  },
  syncMainProject: async (id, opts = {}) => {
    const res = await api<SyncMainProjectResult>('POST', `/projects/${id}/sync-main`, {
      force: !!opts.force,
    });
    const nowIso = now();
    const prev = get().projects.find((p) => p.id === id);
    set({
      projects: get().projects.map((p) =>
        p.id === id
          ? {
              ...p,
              mainProjectId: res.mainProjectId,
              mainProjectNo: res.projectNo,
              mainProjectLinkedAt: p.mainProjectLinkedAt || nowIso,
              mainProjectLastSyncedAt: nowIso,
              mainProjectSyncStatus: 'linked',
              mainProjectSyncError: '',
              mainProjectStatus: p.mainProjectStatus || 'Not Started',
              mainProjectProgressPercent: p.mainProjectProgressPercent ?? 0,
              mainProjectStatusSyncedAt: nowIso,
            }
          : p,
      ),
    });
    // If this project is already won, re-run execution layout so a PCS-named
    // folder gets renamed to `IOCT####-CUST Name` now that we have the number.
    if (prev?.status === 'won' && res.projectNo && isCorporateOneDriveConfigured()) {
      // Reuse updateProject's OneDrive side-effect by applying mainProjectNo again.
      // Avoid a second Firestore write of unrelated fields — only trigger ensure.
      (async () => {
        try {
          const odStore = getOneDriveTokenStore();
          if (!odStore.isAuthenticated) return;
          const token = await odStore.getToken();
          if (!token) return;
          const cur = get().projects.find((p) => p.id === id);
          if (!cur) return;
          const { executionFolder, proposalFolder } = await ensureWonExecutionLayout(token, cur);
          const patchFields: Partial<Project> = {
            executionFolderId: executionFolder.id,
            executionFolderUrl: executionFolder.webUrl,
            ...(proposalFolder?.webUrl ? { proposalFolderUrl: proposalFolder.webUrl } : {}),
          };
          await api('PUT', `/projects/${id}`, patchFields);
          if (cur.mainProjectId) {
            const mainResp = await fetch(`/api/projects/${cur.mainProjectId}`).then((r) => r.json()).catch(() => null);
            if (mainResp && !mainResp.executionFolderId) {
              await api('PUT', `/api/projects/${cur.mainProjectId}`, {
                executionFolderId: executionFolder.id,
                executionFolderUrl: executionFolder.webUrl,
              }).catch(() => {});
            }
          }
          set({
            projects: get().projects.map((proj) =>
              proj.id === id ? { ...proj, ...patchFields } : proj,
            ),
          });
          // eslint-disable-next-line no-console
          console.info('[OneDrive] execution folder renamed after sync-main', executionFolder.name || executionFolder.webUrl);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[OneDrive] post-sync-main rename failed (non-blocking)', err);
        }
      })();
    }
    return res;
  },

  // ── Quotations ─────────────────────────────────────────────────────────────

  createQuotation: async (projectId, kind, recipientId, scopeCategory = 'both') => {
    // Resolve the project's account manager (salesContact) name and seed it into
    // both signatory fields. Editor remains editable; this just gives a sensible
    // default so the PDF "Prepared by:" starts populated with the AM.
    const project = get().projects.find((p) => p.id === projectId);
    const amName = project?.salesContactId
      ? (get().salesContacts.find((sc) => sc.id === project.salesContactId)?.name ?? '')
      : '';
    const defaultTitle = get().settings.defaultJobTitles?.[kind] || undefined;
    const q: Quotation = { ...blankQuotation(projectId, kind, recipientId, nanoid(8), amName, scopeCategory), preparedByTitle: defaultTitle };
    const res = await api<{ quotation: Quotation }>('POST', '/quotations', q);
    const saved = res.quotation ?? q;
    set({ quotations: [...get().quotations, saved] });
    return saved;
  },
  updateQuotation: async (id, patch, remarks) => {
    // Firestore rejects `undefined` values with a 500. Substitute null (to
    // clear the field) so callers can safely send "I have no value here".
    // Undefined keys are dropped entirely when they would just be left alone.
    const cleaned: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(patch)) {
      if (v !== undefined) cleaned[k] = v;
    }
    // `remarks` rides alongside the patch but isn't a Quotation field — the
    // server strips it before writing the document and attaches it to the
    // version snapshot instead.
    if (remarks !== undefined) cleaned.remarks = remarks;
    await api('PUT', `/quotations/${id}`, cleaned);
    const updatedAt = now();
    let saved: Quotation | null = null;
    set({
      quotations: get().quotations.map((q) => {
        if (q.id !== id) return q;
        saved = { ...q, ...patch, updatedAt };
        return saved;
      }),
    });
    if (!saved) throw new Error('Quotation not found after update');
    return saved;
  },
  deleteQuotation: async (id) => {
    await api('DELETE', `/quotations/${id}`);
    set({ quotations: get().quotations.filter((q) => q.id !== id) });
  },
  fetchQuotationVersions: async (id) => {
    const res = await api<{ versions: QuotationVersion[] }>('GET', `/quotations/${id}/versions`);
    return res.versions ?? [];
  },
  deleteQuotationVersion: async (quotationId, versionId) => {
    await api('DELETE', `/quotations/${quotationId}/versions/${versionId}`);
  },
  importQuotation: async (q) => {
    const res = await api<{ quotation: Quotation }>('POST', '/quotations', q);
    const saved = res.quotation ?? q;
    set({ quotations: [...get().quotations, saved] });
    return saved;
  },
  duplicateQuotation: async (id, opts) => {
    const original = get().quotations.find((q) => q.id === id);
    if (!original) return null;
    const targetProjectId = opts?.projectId ?? original.projectId;
    const sameProject = String(targetProjectId) === String(original.projectId);

    // Same-project duplicates bump the revision; cross-project duplicates start
    // fresh at "00". Either way, skip past any revision that already exists for
    // that (project, kind) — the server enforces no uniqueness on this pair.
    const siblingRevisions = get().quotations
      .filter((q) => String(q.projectId) === String(targetProjectId) && q.kind === original.kind)
      .map((q) => parseInt(q.revision, 10))
      .filter((n) => !Number.isNaN(n));
    let nextRevisionNum = sameProject ? parseInt(original.revision, 10) + 1 : 0;
    while (siblingRevisions.includes(nextRevisionNum)) nextRevisionNum += 1;

    const copy: Quotation = {
      ...original,
      id: nanoid(8),
      projectId: targetProjectId,
      revision: String(nextRevisionNum).padStart(2, '0'),
      createdAt: now(),
      updatedAt: now(),
    };
    // A duplicate is a new, unlocked revision (e.g. a change order on an awarded quotation).
    delete copy.awardedAt;
    delete copy.awardedBy;
    delete copy.awardedById;
    // Duplicating a legacy snapshot produces an editable revision under the current formulation.
    // importedFrom is preserved for provenance.
    if (original.formulaVersion === 'legacy') {
      copy.formulaVersion = 'current';
      delete copy.legacyTotalsSnapshot;
      delete copy.generalReqContingencyMode;
    }
    const res = await api<{ quotation: Quotation }>('POST', '/quotations', copy);
    const saved = res.quotation ?? copy;
    set({ quotations: [...get().quotations, saved] });
    return saved;
  },

  // ── Presets ────────────────────────────────────────────────────────────────

  addPreset: async (p) => {
    const preset: LaborRolePreset = { id: nanoid(8), ...p };
    const res = await api<{ preset: LaborRolePreset }>('POST', '/presets', preset);
    const saved = res.preset ?? preset;
    set({ laborPresets: [...get().laborPresets, saved] });
    return saved;
  },
  updatePreset: async (id, patch) => {
    await api('PUT', `/presets/${id}`, patch);
    set({ laborPresets: get().laborPresets.map((p) => (p.id === id ? { ...p, ...patch } : p)) });
  },
  deletePreset: async (id) => {
    await api('DELETE', `/presets/${id}`);
    set({ laborPresets: get().laborPresets.filter((p) => p.id !== id) });
  },
  resetPresets: async () => {
    const defaults = seedLaborPresets();
    // Delete all existing presets
    const current = get().laborPresets;
    await Promise.all(current.map((p) => api('DELETE', `/presets/${p.id}`).catch(() => {})));
    // Re-seed
    const saved = await Promise.all(
      defaults.map((p) => api<{ preset: LaborRolePreset }>('POST', '/presets', p).then((r) => r.preset ?? p)),
    );
    set({ laborPresets: saved });
  },

  // ── Scope Library ────────────────────────────────────────────────────────────
  addScopeBundle: async (b) => {
    const res = await api<{ bundle: ScopeBundle }>('POST', '/scope-library', b);
    // The server stamps id/createdBy/timestamps and echoes the full bundle back.
    // Guard against a malformed 200 so we never push an undefined row into state
    // (which would crash the library list on the next render).
    const saved = res.bundle;
    if (!saved || !saved.id) throw new Error('Server did not return the saved bundle');
    set({ scopeBundles: [saved, ...get().scopeBundles] });
    return saved;
  },
  updateScopeBundle: async (id, patch) => {
    await api('PUT', `/scope-library/${id}`, patch);
    const now = new Date().toISOString();
    set({ scopeBundles: get().scopeBundles.map((b) => (b.id === id ? { ...b, ...patch, updatedAt: now } : b)) });
  },
  deleteScopeBundle: async (id) => {
    await api('DELETE', `/scope-library/${id}`);
    set({ scopeBundles: get().scopeBundles.filter((b) => b.id !== id) });
  },

  updateInstallMaterialPrice: async (key, patch) => {
    await api('PUT', `/install-materials/${key}`, patch);
    set({ installMaterialPrices: { ...get().installMaterialPrices, [key]: patch } });
  },

  updateSettings: async (patch) => {
    await api('PUT', '/settings', patch);
    set((s) => ({ settings: { ...s.settings, ...patch, defaultJobTitles: { ...s.settings.defaultJobTitles, ...patch.defaultJobTitles } } }));
  },

  resetAll: async () => {
    // Clear all data from API. Clients live in the unified /api/clients collection.
    const { projects, quotations, clients, laborPresets } = get();
    await Promise.all([
      ...projects.map((p) => api('DELETE', `/projects/${p.id}`).catch(() => {})),
      ...quotations.map((q) => api('DELETE', `/quotations/${q.id}`).catch(() => {})),
      ...clients.map((c) => api('DELETE', `/api/clients/${c.id}`).catch(() => {})),
      ...laborPresets.map((p) => api('DELETE', `/presets/${p.id}`).catch(() => {})),
    ]);
    const [seededClients, seededPresets] = await Promise.all([
      Promise.all(seedClients().map((c) => api<Client>('POST', '/api/clients', c).catch(() => c))),
      Promise.all(seedLaborPresets().map((p) => api<{ preset: LaborRolePreset }>('POST', '/presets', p).then((r) => r.preset ?? p))),
    ]);
    set({
      clients: seededClients as Client[],
      salesContacts: seedSalesContacts(),
      laborPresets: seededPresets,
      projects: [],
      quotations: [],
      seq: 1,
    });
  },
}));

export const projectQuotations = (projectId: ID) =>
  useQuotationStore.getState().quotations.filter((q) => q.projectId === projectId);
